import {
	ApiError,
	type PickupRequest,
	type PickupRequestDraft,
	type PickupRequestStatus,
} from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import { PICKUP_STATUSES, PickupDrawer, PickupStatusChip, todayIn } from "@khazzin/ui/patterns";
import { EmptyState } from "@khazzin/ui/patterns/page";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import { Button, Dialog, Field, Input, SegmentedControl } from "@khazzin/ui/primitives";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import type { Level } from "../../app/shell/sections";

/**
 * Orders → Pickup requests (EP03-S21, DA-03-08, DA-10-08): the store asks
 * Khazzin to collect goods from its shop, a supplier or a customer, and
 * follows them. The drawer is the console's own (no store picker: the store
 * is the active one) with the fee its plan charges for one pickup that day,
 * and a new request starts from the last pickup's place — the store has no
 * address on file (EP10-S14). A request is edited and cancelled only while
 * nobody has scheduled it; after that it is the dispatcher's.
 */

export function portalPickupsKey(storeId: string) {
	return ["portal", storeId, "pickups"] as const;
}

type Filter = "all" | PickupRequestStatus;

const FILTERS: readonly Filter[] = ["all", ...PICKUP_STATUSES];

/** The edit body: everything but the store. */
function patchOf(draft: PickupRequestDraft) {
	const { storeId: _store, ...patch } = draft;
	return patch;
}

export function PickupsTab({
	storeId,
	level,
	timeZone,
}: {
	storeId: string;
	level: Level;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const [filter, setFilter] = useState<Filter>("all");
	const [drawer, setDrawer] = useState<{ pickup?: PickupRequest } | null>(null);
	const [cancelling, setCancelling] = useState<PickupRequest | null>(null);
	const [reason, setReason] = useState("");
	const [notice, setNotice] = useState<string | null>(null);
	const status = filter === "all" ? undefined : filter;
	const writable = level === "write";
	const refresh = () => void queryClient.invalidateQueries({ queryKey: portalPickupsKey(storeId) });

	const counts = useQuery({
		queryKey: [...portalPickupsKey(storeId), "counts"],
		queryFn: ({ signal }) => api.listPortalPickupRequests({ limit: 1, signal }),
		enabled: storeId !== "",
	});
	const page = useCursorPagination<PickupRequest>(
		(cursor, limit, signal) =>
			api
				.listPortalPickupRequests({
					...(status === undefined ? {} : { status }),
					...(cursor === null ? {} : { cursor }),
					limit,
					signal,
				})
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...portalPickupsKey(storeId), "list", filter], enabled: storeId !== "" },
	);

	const tally = counts.data?.counts ?? {};
	const countOf = (f: Filter) =>
		f === "all" ? Object.values(tally).reduce((sum, n) => sum + n, 0) : (tally[f] ?? 0);

	const columns: Column<PickupRequest>[] = [
		{
			id: "number",
			headerKey: "delivery.pickups.columns.number",
			mono: true,
			card: "wide",
			cell: (p) => p.number,
		},
		{
			id: "type",
			headerKey: "delivery.pickups.columns.type",
			cell: (p) => t(`delivery.pickups.type.${p.pickupType}`),
		},
		{
			id: "origin",
			headerKey: "delivery.pickups.columns.origin",
			cell: (p) => [p.origin.cityName, p.origin.address].filter(Boolean).join(" — "),
		},
		{
			id: "date",
			headerKey: "delivery.pickups.columns.date",
			cell: (p) => `${p.requestedDate} · ${t(`delivery.pickups.window.${p.window}`)}`,
		},
		{
			id: "expected",
			headerKey: "delivery.pickups.columns.expected",
			align: "end",
			cell: (p) => p.expectedCount,
		},
		{
			id: "status",
			headerKey: "delivery.pickups.columns.status",
			cell: (p) => <PickupStatusChip status={p.status} t={t} />,
		},
		{
			id: "fee",
			headerKey: "delivery.pickups.columns.fee",
			align: "end",
			cell: (p) => (p.fee ? formatMoney(p.fee, locale) : "—"),
		},
		{
			id: "actions",
			headerKey: "delivery.pickups.columns.actions",
			cell: (p) =>
				writable ? (
					<div className="flex flex-wrap gap-1">
						{p.status === "requested" && (
							<Button size="sm" variant="secondary" onClick={() => setDrawer({ pickup: p })}>
								{t("delivery.pickups.actions.edit")}
							</Button>
						)}
						{p.status === "requested" && (
							<Button size="sm" variant="ghost" onClick={() => setCancelling(p)}>
								{t("delivery.pickups.actions.cancel")}
							</Button>
						)}
					</div>
				) : null,
		},
	];

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="overflow-x-auto">
					<SegmentedControl<Filter>
						aria-label={t("delivery.pickups.columns.status")}
						value={filter}
						onChange={(next) => {
							setFilter(next);
							page.reset();
						}}
						options={FILTERS.map((f) => ({
							value: f,
							label: t("delivery.pickups.tab", {
								label: t(
									f === "all" ? "delivery.pickups.status.all" : `delivery.pickups.status.${f}`,
								),
								count: countOf(f),
							}),
						}))}
					/>
				</div>
				{writable && (
					<Button variant="primary" className="min-h-12 md:min-h-8" onClick={() => setDrawer({})}>
						{t("delivery.pickups.actions.new")}
					</Button>
				)}
			</div>
			{notice !== null && (
				<p role="status" className="text-[12.5px] text-fg">
					{notice}
				</p>
			)}
			<DataTable<PickupRequest>
				screenKey="portal.orders.pickups"
				caption={t("portal.orders.tabs.pickups")}
				t={t}
				columns={columns}
				rowKey={(p) => p.pickupId}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				empty={
					<EmptyState
						title={t("delivery.pickups.empty.title")}
						body={t("portal.orders.pickups.emptyBody")}
					/>
				}
			/>
			{drawer !== null && (
				<PickupDrawer
					open
					onOpenChange={(open) => !open && setDrawer(null)}
					t={t}
					today={todayIn(timeZone)}
					pickup={drawer.pickup}
					startFrom={page.page?.items[0]?.origin}
					feePreview={(date) => <PickupFee date={date} storeId={storeId} />}
					searchCities={async (query, signal) => {
						if (query.trim() === "") {
							return [];
						}
						const found = await api.searchPortalCities(query, { signal });
						return found.items.map((c) => ({
							cityId: c.cityId,
							cityName: locale === "ar" && c.nameAr !== "" ? c.nameAr : c.name,
							provinceId: c.provinceId,
							provinceName: c.provinceName,
						}));
					}}
					onSubmit={async (draft) => {
						const saved =
							drawer.pickup === undefined
								? await api.createPortalPickupRequest(draft)
								: await api.patchPortalPickupRequest(
										drawer.pickup.pickupId,
										patchOf(draft),
										drawer.pickup.version,
									);
						setNotice(t("delivery.pickups.toast.saved", { number: saved.number }));
						refresh();
					}}
				/>
			)}
			{cancelling !== null && (
				<Dialog
					open
					onOpenChange={(open) => !open && setCancelling(null)}
					title={t("delivery.pickups.dialog.cancelTitle", { number: cancelling.number })}
					closeLabel={t("ui.actions.close")}
					footer={
						<>
							<Button variant="secondary" onClick={() => setCancelling(null)}>
								{t("ui.actions.cancel")}
							</Button>
							<Button
								variant="danger"
								onClick={() => {
									const target = cancelling;
									setCancelling(null);
									api
										.cancelPortalPickupRequest(target.pickupId, reason.trim())
										.then(
											(next) =>
												setNotice(t("delivery.pickups.toast.cancel", { number: next.number })),
											(error: unknown) =>
												setNotice(
													error instanceof ApiError
														? error.message
														: t("delivery.pickups.toast.refused"),
												),
										)
										.finally(refresh);
									setReason("");
								}}
							>
								{t("delivery.pickups.actions.cancel")}
							</Button>
						</>
					}
				>
					<Field label={t("delivery.pickups.dialog.reason")} optional>
						{(control) => (
							<Input {...control} value={reason} onChange={(e) => setReason(e.target.value)} />
						)}
					</Field>
				</Dialog>
			)}
		</div>
	);
}

/**
 * What one pickup costs the store on the day asked for, under its plan
 * (EP10-S14). The charge itself is taken when the driver collects.
 */
function PickupFee({ date, storeId }: { date: string | null; storeId: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const fee = useQuery({
		queryKey: [...portalPickupsKey(storeId), "fee", date],
		queryFn: ({ signal }) => api.getPortalPickupFeePreview(date ?? undefined, { signal }),
	});
	if (fee.data === undefined) {
		return null;
	}
	return (
		<p role="status" className="text-[12.5px] text-fg md:col-span-2">
			{fee.data.rateFound
				? t("portal.orders.pickups.fee", {
						amount: formatMoney(
							{ amountMinor: fee.data.amountMinor, currency: fee.data.currency },
							locale,
						),
					})
				: t("portal.orders.pickups.noFee")}
		</p>
	);
}
